import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Cropper from 'react-easy-crop';

interface Props {
  onUpload: (file: File) => Promise<void>;
  children: React.ReactNode;
  outputWidth?: number;
  outputHeight?: number;
  quality?: number;
}

type Step = 'idle' | 'menu' | 'crop' | 'camera' | 'uploading';
type Area = { x: number; y: number; width: number; height: number };

// ── canvas helpers ─────────────────────────────────────────────────────────────

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });
}

async function getCroppedImg(
  src: string,
  pixelCrop: Area,
  rotation: number,
  outW: number,
  outH: number,
  quality: number,
): Promise<File> {
  const image = await createImage(src);
  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  // Draw rotated image on safe canvas
  const rotCanvas = document.createElement('canvas');
  rotCanvas.width = safeArea;
  rotCanvas.height = safeArea;
  const rCtx = rotCanvas.getContext('2d')!;
  rCtx.translate(safeArea / 2, safeArea / 2);
  rCtx.rotate((rotation * Math.PI) / 180);
  rCtx.translate(-image.width / 2, -image.height / 2);
  rCtx.drawImage(image, 0, 0);

  // Extract crop pixels
  const offsetX = pixelCrop.x + safeArea / 2 - image.width / 2;
  const offsetY = pixelCrop.y + safeArea / 2 - image.height / 2;
  const cropData = rCtx.getImageData(offsetX, offsetY, pixelCrop.width, pixelCrop.height);

  // Scale to output dimensions
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = pixelCrop.width;
  tempCanvas.height = pixelCrop.height;
  tempCanvas.getContext('2d')!.putImageData(cropData, 0, 0);

  const outCanvas = document.createElement('canvas');
  outCanvas.width = outW;
  outCanvas.height = outH;
  outCanvas.getContext('2d')!.drawImage(tempCanvas, 0, 0, outW, outH);

  return new Promise(resolve =>
    outCanvas.toBlob(
      blob => resolve(new File([blob!], 'avatar.jpg', { type: 'image/jpeg' })),
      'image/jpeg',
      quality,
    ),
  );
}

// ── component ──────────────────────────────────────────────────────────────────

const ImageUploadCrop: React.FC<Props> = ({
  onUpload,
  children,
  outputWidth = 200,
  outputHeight = 200,
  quality = 0.85,
}) => {
  const [step, setStep] = useState<Step>('idle');
  const [imageSrc, setImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPx, setCroppedAreaPx] = useState<Area | null>(null);
  const [camError, setCamError] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  // Preview canvas
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [loadedImg, setLoadedImg] = useState<HTMLImageElement | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Load image into state so canvas effect can depend on it
  useEffect(() => {
    if (!imageSrc) { setLoadedImg(null); return; }
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.onload = () => setLoadedImg(img);
    img.src = imageSrc;
  }, [imageSrc]);

  // Redraw preview canvas whenever crop area or rotation changes
  useEffect(() => {
    const canvas = previewCanvasRef.current;
    if (!canvas || !loadedImg || !croppedAreaPx) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, 48, 48);
    try {
      if (rotation === 0) {
        ctx.drawImage(
          loadedImg,
          croppedAreaPx.x, croppedAreaPx.y, croppedAreaPx.width, croppedAreaPx.height,
          0, 0, 48, 48,
        );
      } else {
        // Rotation: draw on safe canvas first, then crop
        const maxSize = Math.max(loadedImg.width, loadedImg.height);
        const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
        const tmp = document.createElement('canvas');
        tmp.width = safeArea; tmp.height = safeArea;
        const tc = tmp.getContext('2d')!;
        tc.translate(safeArea / 2, safeArea / 2);
        tc.rotate((rotation * Math.PI) / 180);
        tc.translate(-loadedImg.width / 2, -loadedImg.height / 2);
        tc.drawImage(loadedImg, 0, 0);
        const ox = croppedAreaPx.x + safeArea / 2 - loadedImg.width / 2;
        const oy = croppedAreaPx.y + safeArea / 2 - loadedImg.height / 2;
        ctx.drawImage(tmp, ox, oy, croppedAreaPx.width, croppedAreaPx.height, 0, 0, 48, 48);
      }
    } catch { /* skip on canvas security errors */ }
  }, [loadedImg, croppedAreaPx, rotation]);

  // Close menu on outside click
  useEffect(() => {
    if (step !== 'menu') return;
    const handler = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [step]);

  // Start camera stream once video element is mounted
  useEffect(() => {
    if (step !== 'camera') return;
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } } })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      })
      .catch(() => {
        if (!cancelled) setCamError('Камера недоступна. Разрешите доступ в браузере.');
      });
    return () => { cancelled = true; };
  }, [step]);

  // Cleanup stream on unmount
  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const closeModal = useCallback(() => {
    stopStream();
    if (imageSrc.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
    setImageSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPx(null);
    setCamError('');
    setStep('idle');
  }, [imageSrc]);

  const closeMenu = () => setStep('idle');

  const openMenu = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const top = r.bottom + 8 > window.innerHeight - 120 ? r.top - 120 : r.bottom + 8;
      setMenuPos({ top, left: r.left });
    }
    setStep('menu');
  };

  // ── file picker
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setStep('crop');
  };

  // ── camera snap
  const snapPhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    stopStream();
    setImageSrc(canvas.toDataURL('image/jpeg', 0.95));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setStep('crop');
  };

  // ── save cropped result
  const handleSave = async () => {
    if (!croppedAreaPx) return;
    setStep('uploading');
    try {
      const file = await getCroppedImg(imageSrc, croppedAreaPx, rotation, outputWidth, outputHeight, quality);
      await onUpload(file);
      closeModal();
    } catch {
      setStep('crop');
    }
  };

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPx(pixels);
  }, []);

  const approxKb = Math.round((outputWidth * outputHeight * quality * 3) / 1024);

  // ── shared button styles
  const btnGhost: React.CSSProperties = {
    padding: '7px 16px', borderRadius: 9, fontSize: 12, fontWeight: 700,
    background: 'var(--btn-user-form)', color: 'var(--text-color)',
    border: '1px solid var(--border-input)', cursor: 'pointer',
  };
  const btnPrimary: React.CSSProperties = {
    padding: '7px 18px', borderRadius: 9, fontSize: 12, fontWeight: 700,
    border: 'none', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white',
    cursor: 'pointer', boxShadow: '0 3px 12px rgba(37,99,235,.28)',
  };
  const ctrlLabel: React.CSSProperties = {
    fontSize: 9, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase',
    color: 'var(--text-color-calendar-week)', marginBottom: 7,
    display: 'flex', alignItems: 'center', gap: 5,
  };

  const isModalOpen = step === 'crop' || step === 'camera' || step === 'uploading';

  return (
    <>
      {/* ── TRIGGER ── */}
      <div ref={triggerRef} style={{ position: 'relative', display: 'inline-block' }}
        onClick={openMenu}>
        {children}
      </div>

      {/* ── HIDDEN FILE INPUT ── */}
      <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange}
        style={{ display: 'none' }} />

      {/* ── CONTEXT MENU — via portal to escape Drawer transform stacking context ── */}
      {step === 'menu' && menuPos && ReactDOM.createPortal(
        <div
          data-portal-modal="true"
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999,
            background: 'var(--bg-form, #fff)',
            border: '1px solid var(--border-input, rgba(0,10,60,.1))',
            borderRadius: 12, padding: 6,
            boxShadow: '0 12px 40px rgba(20,40,120,.16)',
            minWidth: 210,
            animation: 'iuc-pop .15s cubic-bezier(.34,1.56,.64,1) both',
          }}
        >
          <CtxItem
            icon="🖼️"
            iconBg="rgba(37,99,235,.1)"
            label="Загрузить фотографию"
            hint="JPG, PNG, WEBP · до 10 МБ"
            onClick={() => {
              setStep('idle');
              setTimeout(() => fileRef.current?.click(), 0);
            }}
          />
          <div style={{ height: 1, background: 'var(--border-input, rgba(0,10,60,.08))', margin: '4px 0' }} />
          <CtxItem
            icon="📷"
            iconBg="rgba(16,185,129,.1)"
            label="Сделать снимок"
            hint="Веб-камера / фронтальная камера"
            onClick={() => setStep('camera')}
          />
        </div>,
        document.body,
      )}

      {/* ── MODAL OVERLAY — via portal to escape Drawer transform stacking context ── */}
      {isModalOpen && ReactDOM.createPortal(
        <div
          data-portal-modal="true"
          style={{
            position: 'fixed', inset: 0, zIndex: 9998,
            background: 'rgba(0,0,0,.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          onMouseDown={e => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div
            style={{
              background: 'var(--bg-form, #fff)',
              borderRadius: 20, overflow: 'hidden',
              width: 560, maxWidth: '100%',
              boxShadow: '0 24px 70px rgba(0,0,0,.28)',
              animation: 'iuc-rise .2s ease both',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px',
              borderBottom: '1px solid var(--border-input, rgba(0,10,60,.08))',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 9, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 16,
                  background: step === 'camera' ? 'rgba(16,185,129,.1)' : step === 'uploading' ? 'rgba(217,119,6,.1)' : 'rgba(37,99,235,.1)',
                }}>
                  {step === 'camera' ? '📷' : step === 'uploading' ? '⏳' : '✂️'}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-color)', letterSpacing: '-.01em' }}>
                    {step === 'camera' ? 'Сделать снимок' : step === 'uploading' ? 'Сохранение…' : 'Обрежьте фото'}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>
                    {step === 'camera'
                      ? 'Наведите камеру и нажмите кнопку'
                      : step === 'uploading'
                      ? 'Обрезка → JPEG → загрузка'
                      : `Квадрат 1:1 · выход ${outputWidth}×${outputHeight} px`}
                  </div>
                </div>
              </div>
              {step !== 'uploading' && (
                <button type="button" onClick={closeModal} style={{
                  width: 28, height: 28, borderRadius: 7,
                  background: 'var(--btn-user-form)', border: '1px solid var(--border-input)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', fontSize: 12, color: 'var(--text-color-calendar-week)',
                }}>✕</button>
              )}
            </div>

            {/* ── CAMERA VIEW ── */}
            {step === 'camera' && (
              <>
                <div style={{ position: 'relative', background: '#04060f', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <video ref={videoRef} autoPlay playsInline muted
                    style={{ width: '100%', maxHeight: 300, objectFit: 'cover', display: 'block' }} />

                  {/* Face guide */}
                  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ position: 'relative', width: 148, height: 185, border: '1.5px dashed rgba(255,255,255,.28)', borderRadius: '50%' }}>
                      {([
                        { top: -1, left: -1, borderTop: '2px solid rgba(255,255,255,.6)', borderLeft: '2px solid rgba(255,255,255,.6)' },
                        { top: -1, right: -1, borderTop: '2px solid rgba(255,255,255,.6)', borderRight: '2px solid rgba(255,255,255,.6)' },
                        { bottom: -1, left: -1, borderBottom: '2px solid rgba(255,255,255,.6)', borderLeft: '2px solid rgba(255,255,255,.6)' },
                        { bottom: -1, right: -1, borderBottom: '2px solid rgba(255,255,255,.6)', borderRight: '2px solid rgba(255,255,255,.6)' },
                      ] as React.CSSProperties[]).map((s, i) => (
                        <div key={i} style={{ position: 'absolute', width: 16, height: 16, ...s }} />
                      ))}
                    </div>
                  </div>

                  {/* LIVE badge */}
                  <div style={{
                    position: 'absolute', top: 10, left: 12,
                    padding: '3px 8px', borderRadius: 6,
                    background: 'rgba(239,68,68,.88)', color: 'white',
                    fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'white', animation: 'iuc-pulse 1s infinite', display: 'inline-block' }} />
                    LIVE
                  </div>

                  {/* Error */}
                  {camError && (
                    <div style={{
                      position: 'absolute', inset: 0, background: 'rgba(0,0,0,.72)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: 20, textAlign: 'center',
                      color: 'rgba(255,255,255,.7)', fontSize: 13, lineHeight: 1.5,
                    }}>
                      ⚠️ {camError}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  borderTop: '1px solid var(--border-input, rgba(0,10,60,.08))',
                }}>
                  <button type="button" onClick={closeModal} style={btnGhost}>Отмена</button>
                  <button type="button" onClick={snapPhoto} disabled={!!camError} style={{
                    ...btnPrimary,
                    display: 'flex', alignItems: 'center', gap: 8,
                    opacity: camError ? .45 : 1, cursor: camError ? 'not-allowed' : 'pointer',
                  }}>
                    <span style={{ width: 13, height: 13, borderRadius: '50%', background: '#ef4444', display: 'inline-block', boxShadow: '0 0 0 3px rgba(239,68,68,.25)', flexShrink: 0 }} />
                    Сделать снимок
                  </button>
                  <div style={{ width: 80 }} />
                </div>
              </>
            )}

            {/* ── CROP EDITOR ── */}
            {step === 'crop' && (
              <>
                <div style={{ display: 'flex' }}>
                  {/* Cropper canvas */}
                  <div style={{ flex: 1, height: 286, position: 'relative', background: '#080e1d' }}>
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      rotation={rotation}
                      aspect={1}
                      cropShape="rect"
                      showGrid
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>

                  {/* Controls sidebar */}
                  <div style={{
                    width: 162, flexShrink: 0,
                    borderLeft: '1px solid var(--border-input, rgba(0,10,60,.08))',
                    padding: '14px 13px',
                    display: 'flex', flexDirection: 'column', gap: 14,
                    background: 'var(--bg-form, #fff)',
                  }}>
                    {/* Zoom */}
                    <div>
                      <div style={ctrlLabel}>
                        <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block', flexShrink: 0 }} />
                        Масштаб
                      </div>
                      <input type="range" min={1} max={3} step={0.05} value={zoom}
                        onChange={e => setZoom(Number(e.target.value))}
                        style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
                        <span>1×</span><span>{zoom.toFixed(1)}×</span><span>3×</span>
                      </div>
                    </div>

                    {/* Rotation */}
                    <div>
                      <div style={ctrlLabel}>
                        <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block', flexShrink: 0 }} />
                        Поворот
                      </div>
                      <input type="range" min={-45} max={45} step={1} value={rotation}
                        onChange={e => setRotation(Number(e.target.value))}
                        style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
                        <span>−45°</span><span>{rotation}°</span><span>+45°</span>
                      </div>
                    </div>

                    {/* Quality (display only) */}
                    <div>
                      <div style={ctrlLabel}>
                        <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block', flexShrink: 0 }} />
                        Качество JPEG
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: '#059669' }}>
                        {Math.round(quality * 100)}%
                      </span>
                    </div>

                    {/* 48px preview */}
                    <div style={{ marginTop: 'auto' }}>
                      <div style={ctrlLabel}>
                        <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2563eb', display: 'inline-block', flexShrink: 0 }} />
                        Предпросмотр
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <canvas
                          ref={previewCanvasRef}
                          width={48}
                          height={48}
                          style={{
                            width: 48, height: 48,
                            borderRadius: '50%',
                            border: '2px solid var(--border-input, rgba(0,10,60,.08))',
                            background: 'var(--btn-user-form)',
                            display: 'block',
                          }}
                        />
                        <span style={{ fontSize: 9, color: 'var(--text-color-calendar-week)', fontWeight: 600 }}>48 px</span>
                      </div>
                    </div>

                    {/* Output info */}
                    <div style={{ paddingTop: 10, borderTop: '1px solid var(--border-input, rgba(0,10,60,.05))' }}>
                      <div style={{ fontSize: 9, color: 'var(--text-color-calendar-week)', lineHeight: 1.8 }}>
                        📐 <b style={{ color: 'var(--text-color-calendar)' }}>{outputWidth}×{outputHeight} px</b><br />
                        📄 <b style={{ color: 'var(--text-color-calendar)' }}>JPEG</b><br />
                        🗜️ ~<b style={{ color: '#059669' }}>{approxKb} KB</b>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '11px 20px',
                  borderTop: '1px solid var(--border-input, rgba(0,10,60,.08))',
                  background: 'var(--btn-user-form)',
                }}>
                  <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
                    Выход: <b style={{ color: 'var(--text-color-calendar)' }}>{outputWidth}×{outputHeight} px · JPEG · ~{approxKb} KB</b>
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={closeModal} style={btnGhost}>Отмена</button>
                    <button type="button" onClick={handleSave} style={btnPrimary}>
                      ✓ Сохранить аватар
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── UPLOADING ── */}
            {step === 'uploading' && (
              <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'rgba(37,99,235,.08)', border: '2px solid rgba(37,99,235,.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    border: '3px solid rgba(37,99,235,.2)', borderTopColor: '#2563eb',
                    animation: 'iuc-spin .7s linear infinite',
                  }} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-color)', marginBottom: 6 }}>
                  Сохранение фото…
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
                  Обрезка → JPEG → загрузка на сервер
                </div>
              </div>
            )}

          </div>
        </div>,
        document.body,
      )}

      <style>{`
        @keyframes iuc-pop   { from{opacity:0;transform:scale(.94) translateY(-4px)} to{opacity:1;transform:none} }
        @keyframes iuc-rise  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes iuc-spin  { to{transform:rotate(360deg)} }
        @keyframes iuc-pulse { 0%,100%{opacity:1} 50%{opacity:.2} }
      `}</style>
    </>
  );
};

// ── small helper component ─────────────────────────────────────────────────────

function CtxItem({ icon, iconBg, label, hint, onClick }: {
  icon: string; iconBg: string; label: string; hint: string; onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, cursor: 'pointer', transition: 'background .12s' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--btn-user-form, rgba(0,0,0,.04))')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{ width: 28, height: 28, borderRadius: 8, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-color, #1a2540)' }}>{label}</div>
        <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week, #9aaabf)', marginTop: 1 }}>{hint}</div>
      </div>
    </div>
  );
}

export default ImageUploadCrop;
